import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import {
  mapDicMajorAliasMutationPayload,
  mapDicMajorListQueryParams,
  mapDicMajorMutationPayload,
  type DicMajorAliasItemRaw,
  type DicMajorItemRaw,
  type DicMajorListPageRaw,
} from "../api/adapters";
import type {
  DicMajorAliasItem,
  DicMajorAliasMutation,
  DicMajorDeleteResult,
  DicMajorItem,
  DicMajorListPage,
  DicMajorListParams,
  DicMajorMutation,
} from "../types";

/** 查询标准专业分页列表。 */
export async function fetchDicMajorList(params: DicMajorListParams): Promise<DicMajorListPage> {
  const response = await httpClient.get<ApiEnvelope<DicMajorListPageRaw>>("/dic/majors", {
    params: mapDicMajorListQueryParams(params),
  });
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("标准专业列表接口返回了无效数据");
  }
  return mapDicMajorListPage(response.data.data);
}

/** 创建标准专业。 */
export async function createDicMajor(payload: DicMajorMutation): Promise<DicMajorItem> {
  const response = await httpClient.post<ApiEnvelope<DicMajorItemRaw>>(
    "/dic/majors",
    mapDicMajorMutationPayload(payload),
    { timeout: 180_000, "axios-retry": { retries: 0 } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("标准专业创建接口返回了无效数据");
  }
  return mapDicMajorItem(response.data.data);
}

/** 更新标准专业。 */
export async function updateDicMajor(
  majorId: string,
  payload: DicMajorMutation,
): Promise<DicMajorItem> {
  const response = await httpClient.put<ApiEnvelope<DicMajorItemRaw>>(
    `/dic/majors/${majorId}`,
    mapDicMajorMutationPayload(payload),
    { timeout: 180_000, "axios-retry": { retries: 0 } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("标准专业更新接口返回了无效数据");
  }
  return mapDicMajorItem(response.data.data);
}

/** 创建标准专业别名。 */
export async function createDicMajorAlias(
  majorId: string,
  payload: DicMajorAliasMutation,
): Promise<DicMajorAliasItem> {
  const response = await httpClient.post<ApiEnvelope<DicMajorAliasItemRaw>>(
    `/dic/majors/${majorId}/aliases`,
    mapDicMajorAliasMutationPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("标准专业别名创建接口返回了无效数据");
  }
  return mapDicMajorAliasItem(response.data.data);
}

/** 更新标准专业别名。 */
export async function updateDicMajorAlias(
  majorId: string,
  majorAliasId: string,
  payload: DicMajorAliasMutation,
): Promise<DicMajorAliasItem> {
  const response = await httpClient.put<ApiEnvelope<DicMajorAliasItemRaw>>(
    `/dic/majors/${majorId}/aliases/${majorAliasId}`,
    mapDicMajorAliasMutationPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("标准专业别名更新接口返回了无效数据");
  }
  return mapDicMajorAliasItem(response.data.data);
}

/** 删除标准专业别名。 */
export async function deleteDicMajorAlias(
  majorId: string,
  majorAliasId: string,
): Promise<DicMajorDeleteResult> {
  const response = await httpClient.delete<ApiEnvelope<DicMajorDeleteResult>>(
    `/dic/majors/${majorId}/aliases/${majorAliasId}`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("标准专业别名删除接口返回了无效数据");
  }
  return response.data.data;
}

/** 映射标准专业分页响应。 */
function mapDicMajorListPage(raw: DicMajorListPageRaw): DicMajorListPage {
  return {
    items: (raw.items ?? []).map(mapDicMajorItem),
    page: raw.page,
    pageSize: raw.page_size,
    total: raw.total,
  };
}

/** 映射标准专业响应项。 */
function mapDicMajorItem(raw: DicMajorItemRaw): DicMajorItem {
  return {
    vectorSyncWarning: raw.vector_sync_warning ?? undefined,
    majorId: raw.major_id,
    majorName: raw.major_name,
    majorCategory: raw.major_category,
    spellCode: raw.spell_code,
    sortNo: raw.sort_no,
    isDisabled: raw.is_disabled,
    isDisabledLabel: raw.is_disabled_label,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    remark: raw.remark,
    aliases: (raw.aliases ?? []).map(mapDicMajorAliasItem),
  };
}

/** 映射标准专业别名响应项。 */
function mapDicMajorAliasItem(raw: DicMajorAliasItemRaw): DicMajorAliasItem {
  return {
    majorAliasId: raw.major_alias_id,
    aliasName: raw.alias_name,
    majorId: raw.major_id,
    isDisabled: raw.is_disabled,
    isDisabledLabel: raw.is_disabled_label,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    remark: raw.remark,
  };
}
